import { useState } from "react";
import { Mail, X, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Soft verification reminder banner.
 *
 * Shows at the top of the app for users whose email isn't verified yet.
 * Dismissable per-session via localStorage (`lumivera_dismiss_verify`).
 * Lets them resend the verification email in one tap.
 */
export default function VerificationBanner() {
  const { user, fetchMe } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("lumivera_dismiss_verify") === "1"
  );
  const [resending, setResending] = useState(false);

  if (!user || user.email_verified || dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem("lumivera_dismiss_verify", "1");
    setDismissed(true);
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      toast.success("Verification email sent", {
        description: `Check ${user.email} (including Spam / Promotions).`,
      });
    } catch (e) {
      toast.error("Could not resend", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setResending(false);
      // Pick up server-side state in case the user already verified meanwhile.
      fetchMe();
    }
  };

  return (
    <div
      className="w-full bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200/70 dark:border-amber-800/60 px-4 py-2.5"
      data-testid="verification-banner"
    >
      <div className="max-w-5xl mx-auto flex items-center gap-3 flex-wrap">
        <Mail strokeWidth={1.5} className="size-4 text-amber-700 dark:text-amber-300 shrink-0" />
        <div className="text-xs sm:text-sm text-amber-900 dark:text-amber-100 flex-1 min-w-[180px]">
          <span className="font-semibold">Verify your email</span>{" "}
          <span className="text-amber-800/80 dark:text-amber-200/80">
            so you don&apos;t lose access. We sent a link to{" "}
            <span className="font-mono">{user.email}</span>.
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={resend}
          disabled={resending}
          className="rounded-lg h-7 px-2.5 text-[11px] border-amber-300 dark:border-amber-700"
          data-testid="verification-banner-resend"
        >
          <RefreshCcw strokeWidth={1.5} className="size-3 mr-1" />
          {resending ? "Sending…" : "Resend"}
        </Button>
        <button
          onClick={dismiss}
          className="text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100"
          aria-label="Dismiss"
          data-testid="verification-banner-dismiss"
        >
          <X strokeWidth={1.5} className="size-4" />
        </button>
      </div>
    </div>
  );
}
